import { useEffect, useImperativeHandle, useState } from "react";
import { useCallback } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./command";
import type { MentionableItem } from "./text-editor";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

export interface MentionListHandle {
  onKeyDown: (event: { event: KeyboardEvent }) => boolean;
}

export interface MentionListProps {
  items: MentionableItem[];
  command: (item: { id: string; label: string }) => void;
  noResultsMessage?: string;
  ref?: React.Ref<MentionListHandle>;
}

export const MentionList = (props: MentionListProps) => {
  const { items, command, noResultsMessage, ref } = props;
  const [selectedIndex, setSelectedIndex] = useState(
    items.length ? items[0].id : "",
  );

  const selectItem = useCallback(
    (id: string) => {
      const item = items.find((item) => item.id === id);
      if (item) {
        return command({ id: item.id, label: item.name });
      }
    },
    [items, command],
  );

  const upHandler = useCallback(() => {
    const currentIndex = items.findIndex((item) => item.id === selectedIndex);
    const newIndex = (currentIndex + items.length - 1) % items.length;
    setSelectedIndex(items[newIndex].id);
  }, [items, selectedIndex]);

  const downHandler = useCallback(() => {
    const currentIndex = items.findIndex((item) => item.id === selectedIndex);
    const newIndex = (currentIndex + 1) % items.length;
    setSelectedIndex(items[newIndex].id);
  }, [items, selectedIndex]);

  const tabHandler = useCallback(() => {
    const currentIndex = items.findIndex((item) => item.id === selectedIndex);
    const newIndex = (currentIndex + 1) % items.length;
    setSelectedIndex(items[newIndex].id);
  }, [items, selectedIndex]);

  useEffect(() => {
    if (items.length) {
      setSelectedIndex(items[0].id);
    }
  }, [items]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === "ArrowUp") {
          upHandler();
          return true;
        }
        if (event.key === "ArrowDown") {
          downHandler();
          return true;
        }
        if (event.key === "Tab") {
          event.preventDefault();
          tabHandler();
          return true;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }),
    [upHandler, downHandler, tabHandler, selectItem, selectedIndex],
  );

  return (
    <Command className="rounded border border-gray-200 bg-white min-w-12 shadow-toaster">
      <CommandList className="max-h-13 overflow-y-auto pink-scroll">
        <CommandEmpty>{noResultsMessage}</CommandEmpty>
        <CommandGroup>
          {items.map((item) => (
            <CommandItem
              key={item.id}
              onSelect={() => selectItem(item.id)}
              onMouseEnter={() => setSelectedIndex(item.id)}
              className={selectedIndex === item.id ? "bg-gray-50" : ""}
            >
              <Tooltip key={item.id}>
                <TooltipTrigger asChild>
                  <span>{item.name}</span>
                </TooltipTrigger>
                <TooltipContent className="z-[9999]">
                  {item.description}
                </TooltipContent>
              </Tooltip>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

export default MentionList;
